<template>
   <div class="modal fade" id="renewSubscriptionModal" tabindex="-1" role="dialog" aria-labelledby="renewSubscriptionModalLabel">
      <div class="modal-dialog modal-lg" role="document">
         <div class="modal-content">
            <!-- Modal Header -->
            <div class="modal-header bg-primary">
               <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="closeModal">
                  <span aria-hidden="true" style="color: #fff;">&times;</span>
               </button>
               <h4 class="modal-title" id="renewSubscriptionModalLabel">
                  <i class="fa fa-refresh"></i> Renew Subscription
               </h4>
            </div>

            <!-- Modal Body -->
            <div class="modal-body">
               <!-- Alert Messages -->
               <div class="alert alert-success" v-if="success_message">
                  <i class="fa fa-check-circle"></i> {{ success_message }}
               </div>
               <div class="alert alert-danger" v-if="error_message">
                  <i class="fa fa-exclamation-circle"></i> {{ error_message }}
               </div>

               <!-- PIN Info Display -->
               <div class="panel panel-default" v-if="pinData">
                  <div class="panel-body">
                     <div class="row">
                        <div class="col-sm-6 col-xs-12">
                           <div class="form-group">
                              <label class="text-muted small text-uppercase">PIN</label>
                              <p class="lead" style="margin-bottom: 0;">{{ displayUsername }}</p>
                           </div>
                        </div>
                        <div class="col-sm-6 col-xs-12">
                           <div class="form-group">
                              <label class="text-muted small text-uppercase">Current Expiry</label>
                              <p class="lead text-danger" style="margin-bottom: 0;">{{ pinData.validity_date || 'N/A' }}</p>
                           </div>
                        </div>
                     </div>
                     <div class="row">
                        <div class="col-sm-6 col-xs-12">
                           <div class="form-group" style="margin-bottom: 0;">
                              <label class="text-muted small text-uppercase">Current Plan</label>
                              <p class="lead" style="margin-bottom: 0;">{{ pinData.description || 'N/A' }}</p>
                           </div>
                        </div>
                        <div class="col-sm-6 col-xs-12">
                           <div class="form-group" style="margin-bottom: 0;">
                              <label class="text-muted small text-uppercase">Bundle</label>
                              <p class="lead" style="margin-bottom: 0;">{{ pinData.bundle_name || 'N/A' }}</p>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Continue with Current Plan Checkbox - Hidden for free plans (price == 0) -->
               <div class="panel panel-info" v-if="!isCurrentPlanFree">
                  <div class="panel-body">
                     <div class="checkbox" style="margin: 0;">
                        <label style="font-size: 16px;">
                           <input 
                              type="checkbox" 
                              v-model="continueWithCurrentPlan"
                           >
                           <strong>Continue with current plan</strong>
                           <span class="text-muted" v-if="pinData">
                              ({{ pinData.description }} - {{ pinData.no_of_days }} Days)
                           </span>
                        </label>
                     </div>
                  </div>
               </div>

               <!-- Renewal Form -->
               <form class="form-horizontal">
                  <fieldset :disabled="continueWithCurrentPlan">
                     <!-- Bundle Select -->
                     <div class="form-group form-group-lg">
                        <label class="col-sm-3 control-label" for="renew_rate_plan_id">
                           Bundle <span class="text-danger">*</span>
                        </label>
                        <div class="col-sm-9">
                           <select 
                              name="rate_plan_id" 
                              v-model="rate_plan_id" 
                              id="renew_rate_plan_id" 
                              class="form-control input-lg"
                              :disabled="loadingBundles"
                              @change="onBundleChange"
                           >
                              <option value="">{{ loadingBundles ? 'Loading Bundles...' : 'Select Bundle' }}</option>
                              <option v-for="(data) in bundles" :key="data.id" :value="data.id">
                                 {{ data.name }}
                              </option>
                           </select>
                           <p v-if="loadingBundles" class="help-block">
                              <i class="fa fa-spinner fa-spin"></i> Loading bundles...
                           </p>
                           <span v-if="error_rate_plan_id" class="help-block text-danger">
                              <i class="fa fa-warning"></i> {{ error_rate_plan_id }}
                           </span>
                        </div>
                     </div>

                     <!-- Subscription Select -->
                     <div class="form-group form-group-lg">
                        <label class="col-sm-3 control-label" for="renew_subscription_id">
                           Subscription <span class="text-danger">*</span>
                        </label>
                        <div class="col-sm-9">
                           <select 
                              name="subscription_id" 
                              v-model="subscription_id" 
                              id="renew_subscription_id" 
                              class="form-control input-lg"
                              :disabled="!rate_plan_id || loadingDetails"
                              @change="onSubscriptionChange"
                           >
                              <option value="">{{ loadingDetails ? 'Loading Subscriptions...' : 'Select Subscription' }}</option>
                              <option v-for="(data) in ratePlanDetails" :key="data.id" :value="data.id">
                                 {{ data.plan_name }} - {{ data.no_of_days }} Days - {{ currencySymbol }}{{ data.price }}
                              </option>
                           </select>
                           <p v-if="loadingDetails" class="help-block">
                              <i class="fa fa-spinner fa-spin"></i> Loading subscriptions...
                           </p>
                           <span v-if="error_subscription_id" class="help-block text-danger">
                              <i class="fa fa-warning"></i> {{ error_subscription_id }}
                           </span>
                        </div>
                     </div>
                  </fieldset>

                  <!-- Selected Plan Summary -->
                  <div class="form-group" v-if="currentSelectedPlan && !continueWithCurrentPlan">
                     <div class="col-sm-offset-3 col-sm-9">
                        <div class="well well-sm summary-well">
                           <div class="row">
                              <div class="col-xs-12">
                                 <h5 class="summary-title">
                                    <i class="fa fa-check-circle text-success"></i> 
                                    Selected Plan Summary
                                 </h5>
                              </div>
                           </div>
                           <div class="row summary-row">
                              <div class="col-sm-4 col-xs-6">
                                 <div class="summary-item">
                                    <span class="summary-label">Plan Name</span>
                                    <span class="summary-value">{{ currentSelectedPlan.plan_name }}</span>
                                 </div>
                              </div>
                              <div class="col-sm-4 col-xs-6">
                                 <div class="summary-item">
                                    <span class="summary-label">Duration</span>
                                    <span class="summary-value">{{ currentSelectedPlan.no_of_days }} Days</span>
                                 </div>
                              </div>
                              <div class="col-sm-4 col-xs-12">
                                 <div class="summary-item">
                                    <span class="summary-label">Price</span>
                                    <span class="summary-value text-success price-value">{{ currencySymbol }}{{ currentSelectedPlan.price }}</span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </form>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer">
               <button type="button" class="btn btn-default btn-lg" data-dismiss="modal" @click="closeModal" :disabled="isSubmitting">
                  <i class="fa fa-times"></i> Cancel
               </button>
               <button type="button" class="btn btn-success btn-lg" @click="submitRenewal" :disabled="isSubmitting || loadingBundles">
                  <i :class="isSubmitting ? 'fa fa-spinner fa-spin' : 'fa fa-check'"></i>
                  {{ isSubmitting ? 'Processing...' : 'Renew Subscription' }}
               </button>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import axios from 'axios'

export default {
   name: "RenewSubscriptionModal",
   
   props: {
      pinData: {
         type: Object,
         default: null
      },
      userData: {
         type: Object,
         required: true
      }
   },

   data() {
      return {
         bundles: [],
         ratePlanDetails: [],
         rate_plan_id: '',
         subscription_id: '',
         
         continueWithCurrentPlan: false,
         
         success_message: '',
         error_message: '',
         error_rate_plan_id: false,
         error_subscription_id: false,
         
         isSubmitting: false,
         loadingBundles: false,
         loadingDetails: false,
         currencySymbol: '$',
         
         // Store the selected plan details directly
         currentSelectedPlan: null
      }
   },

   computed: {
      displayUsername() {
         if (!this.pinData) return '';
         if (typeof this.com_removeHostedPinPrefix === 'function') {
            return this.com_removeHostedPinPrefix(
               this.pinData.username,
               this.userData.reseller4?.hosted_pin_prefix,
               this.userData.reseller4?.reseller_panel_type
            );
         }
         return this.pinData.username;
      },
      
      isCurrentPlanFree() {
         if (!this.pinData) return false;
         const price = parseFloat(this.pinData.price);
         return price === 0 || isNaN(price);
      }
   },

   watch: {
      pinData: {
         handler(newVal) {
            if (newVal) {
               this.resetForm();
               this.loadBundles();
            }
         },
         immediate: true
      }
   },

   methods: {
      onBundleChange() {
         this.loadRatePlanDetails();
      },
      
      onSubscriptionChange() {
         if (this.subscription_id && this.ratePlanDetails.length) {
            var selectedId = this.subscription_id;
            var found = null;
            for (var i = 0; i < this.ratePlanDetails.length; i++) {
               // Use == for loose comparison (string/number)
               if (this.ratePlanDetails[i].id == selectedId) {
                  found = this.ratePlanDetails[i];
                  break;
               }
            }
            this.currentSelectedPlan = found;
         } else {
            this.currentSelectedPlan = null;
         }
      },
      
      loadBundles() {
         var rootObject = this;
         this.loadingBundles = true;
         
         axios.get('rate-plan?status=1')
            .then(function(response) {
               rootObject.bundles = response.data;
               rootObject.loadingBundles = false;
            })
            .catch(function(error) {
               rootObject.loadingBundles = false;
               rootObject.com_errorHandel(error);
            });
      },

      loadRatePlanDetails() {
         var rootObject = this;
         this.subscription_id = '';
         this.ratePlanDetails = [];
         this.currentSelectedPlan = null;
         this.error_rate_plan_id = false;

         if (!this.rate_plan_id) {
            return;
         }

         this.loadingDetails = true;

         axios.get('get-rates-by-plan-id/' + this.rate_plan_id)
            .then(function(response) {
               var rates = response.data.rates;
               
               // Convert to array if rates is an object (not an array)
               if (rates && !Array.isArray(rates)) {
                  // It's an object with numeric keys like {"0": {...}, "1": {...}}
                  // Convert to array using Object.values()
                  rootObject.ratePlanDetails = Object.values(rates);
               } else {
                  // It's already an array
                  rootObject.ratePlanDetails = rates || [];
               }
               
               rootObject.loadingDetails = false;
            })
            .catch(function(error) {
               rootObject.loadingDetails = false;
               rootObject.com_errorHandel(error);
            });
      },

      validateForm() {
         var hasError = false;
         this.error_rate_plan_id = false;
         this.error_subscription_id = false;

         if (this.continueWithCurrentPlan) {
            return true;
         }

         if (!this.rate_plan_id) {
            this.error_rate_plan_id = 'Please select a bundle';
            hasError = true;
         }

         if (!this.subscription_id) {
            this.error_subscription_id = 'Please select a subscription';
            hasError = true;
         }

         return !hasError;
      },

      submitRenewal() {
         var rootObject = this;
         
         this.success_message = '';
         this.error_message = '';

         if (!this.validateForm()) {
            return;
         }

         this.isSubmitting = true;

         const form = new FormData();
         form.append('username', this.pinData.username);
         
         if (this.continueWithCurrentPlan) {
            form.append('subscription_id', this.pinData.rate_plan_detail_id);
         } else {
            form.append('subscription_id', this.subscription_id);
         }

         var config = {
            headers: { 'Content-Type': 'multipart/form-data' }
         };

         axios.post('renew-subscription', form, config)
            .then(function(response) {
               rootObject.isSubmitting = false;
               
               if (response.data.status_code === 200) {
                  rootObject.success_message = response.data.result || 'Subscription renewed successfully';
                  
                  rootObject.$emit('renewal-success', {
                     pinId: rootObject.pinData.id,
                     newValidityDate: response.data.data?.subscription_validity_date,
                     status: response.data.data?.status
                  });

                  setTimeout(function() {
                     rootObject.closeModal();
                  }, 2000);
               }
            })
            .catch(function(error) {
               rootObject.isSubmitting = false;
               
               if (error.response) {
                  if (error.response.data.errors) {
                     if (typeof error.response.data.errors === 'object') {
                        let errorMessages = [];
                        for (var prop in error.response.data.errors) {
                           errorMessages.push(error.response.data.errors[prop].join(', '));
                        }
                        rootObject.error_message = errorMessages.join(' ');
                     } else {
                        rootObject.error_message = error.response.data.errors;
                     }
                  } else {
                     rootObject.error_message = 'An error occurred while renewing subscription';
                  }
               } else {
                  rootObject.error_message = 'Network error. Please try again.';
               }
               
               rootObject.com_errorHandel(error);
            });
      },

      resetForm() {
         this.rate_plan_id = '';
         this.subscription_id = '';
         this.ratePlanDetails = [];
         this.currentSelectedPlan = null;
         this.continueWithCurrentPlan = false;
         this.success_message = '';
         this.error_message = '';
         this.error_rate_plan_id = false;
         this.error_subscription_id = false;
         this.isSubmitting = false;
         this.loadingBundles = false;
         this.loadingDetails = false;
      },

      closeModal() {
         this.resetForm();
         $('#renewSubscriptionModal').modal('hide');
         this.$emit('modal-closed');
      },

      openModal() {
         $('#renewSubscriptionModal').modal('show');
      }
   },

   mounted() {
      var rootObject = this;
      $('#renewSubscriptionModal').on('hidden.bs.modal', function() {
         rootObject.resetForm();
      });
   },

   beforeDestroy() {
      $('#renewSubscriptionModal').off('hidden.bs.modal');
   }
};
</script>

<style scoped>
/* Modal Header Primary Background */
.modal-header.bg-primary {
   background-color: #3c8dbc;
   color: #fff;
}

.modal-header.bg-primary .modal-title {
   color: #fff;
}

.modal-header.bg-primary .modal-title i {
   margin-right: 10px;
}

/* Text uppercase helper */
.text-uppercase {
   text-transform: uppercase;
   letter-spacing: 0.5px;
}

/* Disabled fieldset styling */
fieldset:disabled {
   opacity: 0.5;
}

/* Summary Well Styling */
.summary-well {
   background: linear-gradient(135deg, #f0fff0 0%, #e8f5e9 100%);
   border: 2px solid #4caf50;
   border-radius: 8px;
   margin-bottom: 0;
}

.summary-title {
   margin: 0 0 15px 0;
   padding-bottom: 10px;
   border-bottom: 1px solid #c8e6c9;
   font-weight: 600;
   color: #2e7d32;
}

.summary-title i {
   margin-right: 8px;
}

.summary-row {
   margin-top: 5px;
}

.summary-item {
   text-align: center;
   padding: 10px 5px;
}

.summary-label {
   display: block;
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 1px;
   color: #666;
   margin-bottom: 5px;
}

.summary-value {
   display: block;
   font-size: 18px;
   font-weight: 600;
   color: #333;
}

.price-value {
   font-size: 22px;
   color: #2e7d32 !important;
}

/* Mobile adjustments */
@media (max-width: 767px) {
   .summary-item {
      padding: 8px 5px;
      border-bottom: 1px solid #e0e0e0;
   }
   
   .summary-item:last-child {
      border-bottom: none;
   }
   
   .summary-value {
      font-size: 16px;
   }
   
   .price-value {
      font-size: 20px;
   }
   
   .col-sm-4.col-xs-12 .summary-item {
      margin-top: 10px;
   }
}
</style>